import { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useFamily } from '../../contexts/FamilyContext';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faPlus, faTrash, faEdit } from '@fortawesome/free-solid-svg-icons';

export default function FamilySettings() {
  const { user } = useAuth();
  const { family, familyId } = useFamily();
  const [loading, setLoading] = useState(false);
  const [familyName, setFamilyName] = useState('');
  const [kids, setKids] = useState([]);
  const [editingKid, setEditingKid] = useState(null);
  const [newKidName, setNewKidName] = useState('');
  const [newKidAge, setNewKidAge] = useState('');

  useEffect(() => {
    if (family) {
      setFamilyName(family.name || '');
      setKids(family.kids || []);
    }
  }, [family]);

  const handleAddKid = () => {
    if (!newKidName.trim() || !newKidAge.trim()) {
      toast.error('Please enter both name and age');
      return;
    }

    const age = parseInt(newKidAge);
    if (isNaN(age) || age < 0 || age > 18) {
      toast.error('Please enter a valid age (0-18)');
      return;
    }

    setKids([...kids, { name: newKidName.trim(), age: age }]);
    setNewKidName('');
    setNewKidAge('');
  };

  const handleRemoveKid = (index) => {
    setKids(kids.filter((_, i) => i !== index));
  };

  const handleEditKid = (index) => {
    setEditingKid(index);
    setNewKidName(kids[index].name);
    setNewKidAge(kids[index].age.toString());
  };

  const handleSaveKidEdit = () => {
    if (!newKidName.trim() || !newKidAge.trim()) {
      toast.error('Please enter both name and age');
      return;
    }

    const age = parseInt(newKidAge);
    if (isNaN(age) || age < 0 || age > 18) {
      toast.error('Please enter a valid age (0-18)');
      return;
    }

    const updatedKids = [...kids];
    updatedKids[editingKid] = { name: newKidName.trim(), age: age };
    setKids(updatedKids);
    setEditingKid(null);
    setNewKidName('');
    setNewKidAge('');
  };

  const handleSave = async () => {
    if (!familyId || !user) return;

    setLoading(true);
    try {
      await updateDoc(doc(db, 'families', familyId), {
        name: familyName,
        kids: kids,
        updatedAt: new Date(),
      });

      toast.success('Family settings updated!');
    } catch (error) {
      console.error('Error updating family:', error);
      toast.error('Failed to update family settings');
    } finally {
      setLoading(false);
    }
  };

  const getPartnerInfo = () => {
    if (!family?.members || family.members.length < 2) return null;
    const partnerId = family.members.find(id => id !== user?.uid);
    return partnerId ? { id: partnerId } : null;
  };

  return (
    <div className="bg-white rounded-card shadow-card border border-border p-6 sm:p-8">
      <h2 className="text-xl sm:text-2xl font-semibold text-text-primary mb-6">Family Settings</h2>

      {/* Family Name */}
      <div className="mb-6">
        <label htmlFor="familyName" className="block text-sm font-medium text-text-primary mb-2">
          Family Name
        </label>
        <input
          type="text"
          id="familyName"
          value={familyName}
          onChange={(e) => setFamilyName(e.target.value)}
          className="w-full px-4 py-2.5 text-base border border-border rounded-lg focus:outline-none focus:border-primary transition-all"
          placeholder="The Lombroso Weissgold Family"
        />
      </div>

      {/* Kids Management */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-text-primary mb-3">
          Kids
        </label>
        
        {/* Existing Kids */}
        <div className="space-y-3 mb-4">
          {kids.map((kid, index) => (
            <div key={index} className="flex items-center gap-3 p-3 bg-surface rounded-lg">
              {editingKid === index ? (
                <>
                  <input
                    type="text"
                    value={newKidName}
                    onChange={(e) => setNewKidName(e.target.value)}
                    className="flex-1 px-3 py-2 text-base border border-border rounded-lg focus:outline-none focus:border-primary"
                    placeholder="Name"
                  />
                  <input
                    type="number"
                    value={newKidAge}
                    onChange={(e) => setNewKidAge(e.target.value)}
                    className="w-20 px-3 py-2 text-base border border-border rounded-lg focus:outline-none focus:border-primary"
                    placeholder="Age"
                    min="0"
                    max="18"
                  />
                  <button
                    onClick={handleSaveKidEdit}
                    className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setEditingKid(null);
                      setNewKidName('');
                      setNewKidAge('');
                    }}
                    className="px-3 py-2 bg-surface text-text-primary rounded-lg hover:bg-[#E0E0E0] transition-colors"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <div className="flex-1">
                    <div className="font-medium text-text-primary">{kid.name}</div>
                    <div className="text-sm text-text-secondary">Age {kid.age}</div>
                  </div>
                  <button
                    onClick={() => handleEditKid(index)}
                    className="p-2 text-text-secondary hover:text-primary transition-colors"
                    title="Edit"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  <button
                    onClick={() => handleRemoveKid(index)}
                    className="p-2 text-text-secondary hover:text-red-600 transition-colors"
                    title="Remove"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Add New Kid */}
        {editingKid === null && (
          <div className="flex gap-3">
            <input
              type="text"
              value={newKidName}
              onChange={(e) => setNewKidName(e.target.value)}
              className="flex-1 px-4 py-2.5 text-base border border-border rounded-lg focus:outline-none focus:border-primary"
              placeholder="Kid's name"
            />
            <input
              type="number"
              value={newKidAge}
              onChange={(e) => setNewKidAge(e.target.value)}
              className="w-24 px-4 py-2.5 text-base border border-border rounded-lg focus:outline-none focus:border-primary"
              placeholder="Age"
              min="0"
              max="18"
            />
            <button
              onClick={handleAddKid}
              className="px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Add</span>
            </button>
          </div>
        )}
      </div>

      {/* Partner Management */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-text-primary mb-3">
          Partner
        </label>
        {getPartnerInfo() ? (
          <div className="p-4 bg-surface rounded-lg">
            <div className="text-sm text-text-secondary mb-2">Partner connected</div>
            <button
              className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              onClick={() => {
                if (window.confirm('Are you sure you want to remove your partner? This action cannot be undone.')) {
                  toast.info('Partner removal coming soon');
                }
              }}
            >
              Remove Partner
            </button>
          </div>
        ) : (
          <div className="p-4 bg-surface rounded-lg">
            <div className="text-sm text-text-secondary mb-3">No partner connected</div>
            <button
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
              onClick={() => {
                // Navigate to invite flow
                window.location.href = '/';
              }}
            >
              Invite Partner
            </button>
          </div>
        )}
      </div>

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={loading}
        className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-all shadow-button hover:shadow-button-hover flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <FontAwesomeIcon icon={faSave} />
        <span>{loading ? 'Saving...' : 'Save Changes'}</span>
      </button>
    </div>
  );
}

